/**
 * HOCP Design System v2.0 — Primitives
 * Every page inherits from these tokens automatically.
 */

export function PageHeader({ title, subtitle, action, children, testid }) {
  const rightSlot = action ?? children;
  return (
    <div
      className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-6 pb-4 mb-4 md:pb-6 md:mb-6 border-b border-hocp-border"
      data-testid={testid}
    >
      <div className="min-w-0">
        {subtitle && (
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-hocp-textTertiary mb-2">
            {subtitle}
          </div>
        )}
        <h1 className="font-display text-[20px] md:text-[24px] font-bold leading-tight tracking-tight text-hocp-textPrimary">
          {title}
        </h1>
      </div>
      {rightSlot && <div className="shrink-0 flex flex-wrap items-center gap-2 [&>button]:max-sm:w-full [&>a]:max-sm:w-full">{rightSlot}</div>}
    </div>
  );
}

export function SectionTitle({ children, hint, action }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-4">
      <div>
        <h2 className="font-display text-[16px] md:text-[18px] font-semibold tracking-tight text-hocp-textPrimary leading-tight">{children}</h2>
        {hint && <div className="text-[13px] text-hocp-textSecondary mt-1">{hint}</div>}
      </div>
      {action}
    </div>
  );
}

export function Card({ children, className = "", padded = false, interactive = false, testid, ...props }) {
  return (
    <div
      className={[
        "hocp-card",
        padded ? "p-4 md:p-6" : "",
        interactive ? "cursor-pointer hover:-translate-y-[1px]" : "",
        className,
      ].filter(Boolean).join(" ")}
      data-testid={testid}
      {...props}
    >
      {children}
    </div>
  );
}

export function Metric({ label, value, hint, trend, icon: Icon, tone = "default", testid }) {
  const toneRing = {
    default: "",
    accent: "ring-1 ring-hocp-honeydew/70",
    highlight: "ring-1 ring-hocp-vanilla/80",
  }[tone] || "";
  const trendCls = (trend || "").startsWith("+")
    ? "text-emerald-600 bg-emerald-50"
    : (trend || "").startsWith("-")
      ? "text-rose-600 bg-rose-50"
      : "text-hocp-textSecondary bg-hocp-alice/50";
  return (
    <div className={`hocp-card p-5 ${toneRing}`} data-testid={testid}>
      <div className="flex items-start justify-between mb-4">
        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-hocp-textTertiary">
          {label}
        </div>
        {Icon && (
          <div className="w-8 h-8 rounded-lg bg-hocp-alice/60 flex items-center justify-center">
            <Icon className="w-4 h-4 text-hocp-textSecondary" strokeWidth={1.75} />
          </div>
        )}
      </div>
      <div className="font-display text-[28px] font-bold tabular-nums leading-none tracking-tight text-hocp-textPrimary">
        {value}
      </div>
      <div className="mt-3 flex items-center gap-2">
        {trend && (
          <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${trendCls}`}>
            {trend}
          </span>
        )}
        {hint && <span className="text-[12px] text-hocp-textSecondary">{hint}</span>}
      </div>
    </div>
  );
}

export function Badge({ tone = "neutral", size = "md", children, className = "", ...rest }) {
  const tones = {
    success: "bg-[var(--success-bg)] text-[var(--success-text)]",
    warning: "bg-[var(--warning-bg)] text-[var(--warning-text)]",
    error:   "bg-[var(--error-bg)] text-[var(--error-text)]",
    info:    "bg-[var(--info-bg)] text-[var(--info-text)]",
    neutral: "bg-[var(--neutral-bg)] text-[var(--neutral-text)]",
    accent:  "bg-hocp-honeydew text-[var(--accent-text)]",
    highlight: "bg-hocp-vanilla text-[var(--highlight-text)]",
  };
  const sizes = {
    sm: "text-[10px] px-1.5 py-0.5",
    md: "text-[11px] px-2 py-0.5",
    lg: "text-[12px] px-2.5 py-1",
  };
  return (
    <span
      {...rest}
      className={`inline-flex items-center gap-1.5 rounded-full font-semibold ${tones[tone]} ${sizes[size]} ${className}`}
    >
      {children}
    </span>
  );
}

export function StatusDot({ tone = "neutral" }) {
  const dots = {
    success: "bg-[var(--success-dot)]",
    warning: "bg-[var(--warning-dot)]",
    error:   "bg-[var(--error-dot)]",
    info:    "bg-[var(--info-dot)]",
    neutral: "bg-hocp-textTertiary",
  };
  return <span className={`inline-block w-1.5 h-1.5 rounded-full ${dots[tone]}`} />;
}

export function EmptyState({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="hocp-card p-12 text-center flex flex-col items-center gap-3">
      {Icon && (
        <div className="w-14 h-14 rounded-2xl bg-hocp-alice/60 flex items-center justify-center mb-2">
          <Icon className="w-7 h-7 text-hocp-textSecondary" strokeWidth={1.5} />
        </div>
      )}
      <div className="font-display font-semibold text-[18px] text-hocp-textPrimary">{title}</div>
      {subtitle && (
        <div className="text-[14px] text-hocp-textSecondary max-w-sm">{subtitle}</div>
      )}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

export function DataTable({ columns, rows, empty = "No records", testid, rowKey, onRowClick, selectedKey, mobileTitleKey }) {
  const keyOf = (row, idx) => (rowKey && row[rowKey]) || row.id || row._id || idx;
  const titleCol = columns.find((c) => c.key === mobileTitleKey) || columns[0];
  const rest = columns.filter((c) => c !== titleCol);
  return (
    <div className="hocp-card overflow-hidden" data-testid={testid}>
      {/* Desktop / tablet — full table (secondary columns hidden on tablet via c.hideOnTablet) */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-[14px]">
          <thead>
            <tr className="bg-hocp-softIce">
              {columns.map((c) => (
                <th key={c.key}
                  className={`px-4 py-3 text-[12px] font-semibold uppercase tracking-[0.08em] text-hocp-deepTeal text-left ${c.align === "right" ? "text-right" : ""} ${c.hideOnTablet ? "hidden lg:table-cell" : ""}`}
                  style={{ width: c.width }}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={columns.length} className="px-4 py-16 text-center text-hocp-textSecondary">{empty}</td></tr>
            ) : rows.map((row, idx) => {
              const k = keyOf(row, idx);
              return (
                <tr key={k} onClick={onRowClick ? () => onRowClick(row) : undefined} data-testid={testid ? `${testid}-row-${k}` : undefined}
                  className={`border-t border-hocp-borderSubtle transition-colors ${selectedKey != null && selectedKey === k ? "bg-hocp-mintSoft" : "hover:bg-hocp-tableHover"} ${onRowClick ? "cursor-pointer" : ""}`}>
                  {columns.map((c) => (
                    <td key={c.key} className={`px-4 py-3 text-hocp-textPrimary align-middle ${c.align === "right" ? "text-right tabular-nums" : ""} ${c.mono ? "font-mono text-hocp-textSecondary text-[13px]" : ""} ${c.hideOnTablet ? "hidden lg:table-cell" : ""}`}>
                      {c.render ? c.render(row) : row[c.key]}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Mobile — rows become cards */}
      <div className="md:hidden divide-y divide-hocp-borderSubtle" data-testid={testid ? `${testid}-cards` : undefined}>
        {rows.length === 0 ? (
          <div className="px-4 py-12 text-center text-hocp-textSecondary text-[14px]">{empty}</div>
        ) : rows.map((row, idx) => {
          const k = keyOf(row, idx);
          return (
            <div key={k} onClick={onRowClick ? () => onRowClick(row) : undefined} data-testid={testid ? `${testid}-card-${k}` : undefined}
              className={`p-4 space-y-2 ${selectedKey != null && selectedKey === k ? "bg-hocp-mintSoft" : "bg-white"} ${onRowClick ? "active:bg-hocp-tableHover cursor-pointer" : ""}`}>
              <div className="text-[15px] font-bold text-hocp-textPrimary leading-snug">{titleCol.render ? titleCol.render(row) : row[titleCol.key]}</div>
              <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                {rest.map((c) => (
                  <div key={c.key} className={`min-w-0 ${c.fullWidthMobile ? "col-span-2" : ""}`}>
                    <dt className="text-[11px] uppercase tracking-wider text-hocp-textMuted">{c.label}</dt>
                    <dd className={`text-[14px] text-hocp-textPrimary break-words ${c.mono ? "font-mono text-[13px]" : ""} ${c.align === "right" ? "font-bold text-hocp-deepTeal tabular-nums" : ""}`}>{c.render ? c.render(row) : row[c.key]}</dd>
                  </div>
                ))}
              </dl>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* Section wrapper for grouping cards on a page */
export function Section({ children, className = "" }) {
  return <section className={`mb-10 ${className}`}>{children}</section>;
}

/* Simple skeleton block */
export function Skeleton({ className = "" }) {
  return <div className={`skeleton h-4 w-full ${className}`} />;
}
